"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu } from "lucide-react";
import { Button as ButtonComponent } from "@/components/ui/button";
import Button from "@/components/Button";
import Navigation from "@/components/Navigation";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { CommonStoryblok, HeaderStoryblok } from "@/component-types-sb";
import { cn } from "@/lib/utils";
import { storyblokEditable } from "@storyblok/react";
import { getStoryblokImageUrl } from "@/lib/storyblok-image";
import OpeningHours from "@/components/OpeningHours";

export default function Header({ headerData, commonData }: Readonly<{ headerData: HeaderStoryblok, commonData: CommonStoryblok }>) {
  const [navVisible, setNavVisible] = useState(true);
  const [scrollingUp, setScrollingUp] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const compactHeaderRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);

  // Track whether main nav is in viewport (desktop behavior)
  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setNavVisible(entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(navElement);
    return () => observer.disconnect();
  }, []);

  // Track scroll direction for mobile show-on-scroll-up behavior
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollingUp(currentScrollY < lastScrollY.current && currentScrollY > 50);
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Determine if compact header is actually visible (for accessibility)
  const compactVisible = scrollingUp || !navVisible;

  useEffect(() => {
    if (!compactHeaderRef.current) return;

    const header = compactHeaderRef.current;
    const focusableElements = header.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (!compactVisible) {
      header.setAttribute("aria-hidden", "true");
      header.setAttribute("inert", "");
      focusableElements.forEach((el) => {
        el.setAttribute("tabindex", "-1");
      });
    } else {
      header.removeAttribute("aria-hidden");
      header.removeAttribute("inert");
      focusableElements.forEach((el) => {
        el.removeAttribute("tabindex");
      });
    }
  }, [compactVisible]);

  return (
    <div {...storyblokEditable(headerData)} className="relative">
      {/* Main header */}
      <header
        className={cn(
          "absolute top-0 left-0 w-full z-40 border-b-1 border-white",
          headerData?.header_fill && "bg-card-muted border-card-muted static",
        )}
      >
        <div className="py-4 px-4 lg:px-14 grid grid-cols-[1fr_auto_1fr]">
          <div className="md:hidden flex justify-start items-center">
            <Sheet>
              <SheetTrigger asChild>
                <ButtonComponent
                  variant={"ghost"}
                  size={"icon"}
                  aria-label="Öffne Menü"
                  className="cursor-pointer"
                >
                  <Menu className="w-5 h-5" />
                </ButtonComponent>
              </SheetTrigger>
              <SheetContent side="left" className="overflow-y-auto">
                <SheetHeader className="w-full flex items-center border-b-1 border-white py-4">
                  <SheetTitle>
                    <p className="sr-only">Menü</p>
                    <Link href="/">
                      <Image
                        src={getStoryblokImageUrl(headerData?.logo?.filename, "256x0/filters:quality(100)")}
                        alt="Sprängi Bar & Cafe Logo"
                        className="object-contain"
                        width={256}
                        height={126}
                        unoptimized
                      />
                    </Link>
                  </SheetTitle>
                </SheetHeader>
                <Navigation
                  commonData={commonData}
                  className="flex-grow justify-center"
                />
                <OpeningHours
                  entries={commonData?.opening_hours}
                  className="flex flex-col items-center gap-2 border-t-1 border-white p-4 text-center"
                />
              </SheetContent>
            </Sheet>
          </div>

          <OpeningHours
            entries={commonData?.opening_hours}
            className="hidden md:flex pl-[0.625rem] flex-col gap-1 justify-center"
          />

          <div className="flex justify-center items-center">
            <Link href="/">
              <Image
                src={getStoryblokImageUrl(headerData?.logo?.filename, "256x0/filters:quality(100)")}
                alt="Sprängi Bar & Cafe Logo"
                className="object-contain"
                width={256}
                height={126}
                loading="eager"
                priority
                unoptimized
              />
            </Link>
          </div>

          <div className="flex justify-end items-center">
            {headerData?.buttons?.[0] && (
              <Button blok={headerData.buttons[0]} />
            )}
            <div className="hidden md:flex items-center">
              {headerData?.buttons?.slice(1).map((button, index) => (
                <Button
                  key={button._uid || index}
                  blok={button}
                />
              ))}
            </div>
          </div>
        </div>

        <div ref={navRef} className="w-full hidden md:block">
          <div className="max-w-[2000px] mx-auto px-4 lg:px-14">
            <Navigation
              commonData={commonData}
              className="flex flex-row flex-wrap gap-4 justify-center py-2"
            />
          </div>
        </div>
      </header>

      {/* Compact header */}
      <div
        ref={compactHeaderRef}
        role="banner"
        className={cn(
          "fixed top-0 left-1/2 -translate-x-1/2 w-full z-50 shadow-md bg-background transition-transform duration-300 ease-in-out max-w-[2000px]",
          scrollingUp ? "translate-y-0 md:-translate-y-full" : "-translate-y-full",
          !navVisible && "md:translate-y-0",
        )}
      >
        <div className="py-4 px-4 lg:px-12 flex items-center justify-between">
          <Navigation commonData={commonData} className="hidden md:flex flex-row" />

          <div className="md:hidden flex justify-start items-center">
            <Sheet>
              <SheetTrigger asChild>
                <ButtonComponent
                  variant={"ghost"}
                  size={"icon"}
                  aria-label="Öffne Menü"
                  className="cursor-pointer"
                >
                  <Menu className="w-5 h-5" />
                </ButtonComponent>
              </SheetTrigger>
              <SheetContent side="left" className="overflow-y-auto">
                <SheetHeader className="w-full flex items-center border-b-1 border-white py-4">
                  <SheetTitle>
                    <p className="sr-only">Menü</p>
                    <Link href="/">
                      <Image
                        src={getStoryblokImageUrl(headerData?.logo?.filename, "256x0/filters:quality(100)")}
                        alt="Sprängi Bar & Cafe Logo"
                        className="object-contain"
                        width={256}
                        height={126}
                        unoptimized
                      />
                    </Link>
                  </SheetTitle>
                </SheetHeader>
                <Navigation
                  commonData={commonData}
                  className="flex-grow justify-center"
                />
                <OpeningHours
                  entries={commonData?.opening_hours}
                  className="flex flex-col items-center gap-2 border-t-1 border-white p-4 text-center"
                />
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex justify-end items-center pr-4">
            <Link href="/">
              <Image
                src={getStoryblokImageUrl(headerData?.logo?.filename, "256x0/filters:quality(100)")}
                alt="Sprängi Bar & Cafe Logo"
                className="object-contain"
                width={256}
                height={126}
                unoptimized
              />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
